import { Stack, Typography } from '@mui/material';
import { Control, Controller } from 'react-hook-form';

import { CompoundMultiSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { MultiSelectInput } from '@/components/form/baseInputs/MultiSelectInput';
import { SelectItemGroupLabel } from '@/components/form/baseInputs/SelectInput/SelectItemGroupLabel';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareSwitch } from '@/components/form/formAwareInputs/FormAwareSwitch';
import { Card } from '@/components/layout/Card/Card';
import { PopperContent } from '@/components/poppers/PopperContent';
import { EntitySummaryEntityType } from '@/types/schema';

import {
  applyEntityTypesSelection,
  CONCRETE_ENTITY_TYPES,
  ENTITY_TYPE_DISPLAY_NAMES,
  EntitySummaryBundleConfigFormShape,
  getEntityTypesDisplayText,
  getEntityTypesSelectValue,
  isEntityTypesSelectionPartial,
  parseEntityTypesFromSelectValue,
} from './EntitySummaryBundleConfigModal.utils';

/**
 * The dropdown's options for a given selection. The select-all row's checked
 * state is derived from the types below it, so its indeterminate state depends
 * on the selection and the options can't be a module constant.
 */
function getEntityTypeOptions(
  selected: EntitySummaryEntityType[]
): CompoundMultiSelectInputOption[] {
  return [
    {
      value: EntitySummaryEntityType.All,
      display: 'Select all',
      indeterminate: isEntityTypesSelectionPartial(selected),
    },
    // Sets the select-all row apart from the types it toggles. A component
    // option with no `value` renders as-is and isn't selectable, and an
    // unlabeled SelectItemGroupLabel is this repo's dropdown divider.
    { type: 'component', component: <SelectItemGroupLabel /> },
    ...CONCRETE_ENTITY_TYPES.map((entityType) => ({
      value: entityType,
      display: ENTITY_TYPE_DISPLAY_NAMES[entityType],
    })),
  ];
}

interface EntitySummaryConfigFieldProps {
  control: Control<EntitySummaryBundleConfigFormShape>;
}

/**
 * The "Entity types" dropdown, whose "All" row is derived from the rest of the
 * selection rather than stored. It uses `MultiSelectInput` directly instead of
 * `FormAwareMultiSelectInput` because that wrapper passes `field.onChange`
 * straight through, and the "All" row needs the raw selection intercepted
 * before it reaches the form.
 */
export function EntityTypesMultiSelect({
  control,
}: EntitySummaryConfigFieldProps) {
  return (
    <Controller
      control={control}
      name="entityTypes"
      render={({ field }) => (
        <MultiSelectInput
          label="Entity types"
          required
          options={getEntityTypeOptions(field.value)}
          name={field.name}
          value={getEntityTypesSelectValue(field.value)}
          renderValue={() => getEntityTypesDisplayText(field.value)}
          onChange={(event) =>
            field.onChange(
              applyEntityTypesSelection({
                current: field.value,
                next: parseEntityTypesFromSelectValue(event.target.value),
              })
            )
          }
          onBlur={field.onBlur}
          inputRef={field.ref}
        />
      )}
    />
  );
}

/**
 * The presentation-notes switch and the "Pages to include" block, mirroring the
 * presentation editor's entity overview modal so a template produces the bundle
 * an advisor would have built by hand.
 */
export function EntitySummaryPagesSelection({
  control,
}: EntitySummaryConfigFieldProps) {
  return (
    <>
      <FormAwareSwitch<EntitySummaryBundleConfigFormShape>
        fieldName="includeEntityNotesOnSummaryPage"
        control={control}
        label="Show presentation notes"
        labelPosition="right"
        contextualHelp={
          <PopperContent body="Shows each entity's presentation notes on its diagram page." />
        }
      />
      <Card variant="filled" sx={{ p: 2, pb: 4 }}>
        <Stack spacing={2}>
          <Typography variant="title-s" component="span">
            Pages to include
          </Typography>
          <Stack spacing={1}>
            <FormAwareCheckbox<EntitySummaryBundleConfigFormShape>
              fieldName="includeEntitySummaryPage"
              control={control}
              label="Cover"
            />
            <FormAwareCheckbox<EntitySummaryBundleConfigFormShape>
              fieldName="includeDiagramAndNotesPage"
              control={control}
              label="Diagram"
            />
            <FormAwareCheckbox<EntitySummaryBundleConfigFormShape>
              fieldName="includeBalanceSheetPage"
              control={control}
              label="Balance sheet"
            />
            <FormAwareCheckbox<EntitySummaryBundleConfigFormShape>
              fieldName="includeBalanceSheetExportPage"
              control={control}
              label="Balance sheet (full sheet)"
              contextualHelp={
                <PopperContent body="Shows the full balance sheet on a single page, even when that may be larger than a standard slide" />
              }
            />
            <FormAwareCheckbox<EntitySummaryBundleConfigFormShape>
              fieldName="includeBeneficiariesAndDispositionsPage"
              control={control}
              label="Current beneficiaries & dispositions"
            />
            <FormAwareCheckbox<EntitySummaryBundleConfigFormShape>
              fieldName="includeInsurancePolicySlides"
              control={control}
              label="Insurance policies"
            />
          </Stack>
        </Stack>
      </Card>
    </>
  );
}
